<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTenant, hasActiveSubscription } from '@/composables/useTenant';
import TenantSwitcher from '@/components/navigation/TenantSwitcher.vue';
import UserMenu from '@/components/navigation/UserMenu.vue';
import LanguageSwitcher from '@/components/navigation/LanguageSwitcher.vue';
import NotificationPopover from '@/components/navigation/NotificationPopover.vue';
import {
  LayoutDashboard,
  Users2,
  CalendarDays,
  Settings,
  ChevronLeft,
  ChevronRight,
  Trophy,
  ClipboardList,
  CreditCard,
  Video,
  Dumbbell,
  Menu,
  X,
  Heart,
  Compass,
  Database,
  Lock,
  AlertTriangle,
  ArrowRight
} from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { activeTenant, currentUser } = useTenant();

const isCollapsed = ref(false);
const isMobileOpen = ref(false);

// Dynamic section label based on active role
const sectionTitle = computed(() => {
  if (currentUser.value.role === 'PLAYER') {
    return t('nav.playerMenu');
  }
  if (currentUser.value.role === 'GUARDIAN' || currentUser.value.role === 'PARENT') {
    return t('nav.parentMenu');
  }
  if (currentUser.value.role === 'COACH' || currentUser.value.role === 'HEAD_COACH' || currentUser.value.role === 'ASSISTANT_COACH') {
    return t('nav.coachMenu');
  }
  return t('nav.adminMenu');
});

// Dynamic role badge styling and label
const roleBadge = computed(() => {
  const role = currentUser.value.role;
  if (role === 'ADMIN' || role === 'ORG_ADMIN' || role === 'TEAM_ADMIN') {
    return { label: t('roles.ADMIN') || 'Tenant Admin', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' };
  }
  if (role === 'COACH' || role === 'HEAD_COACH' || role === 'ASSISTANT_COACH') {
    return { label: t('roles.COACH') || 'Coach', color: 'bg-sky-500/15 text-sky-400 border-sky-500/30' };
  }
  if (role === 'PLAYER') {
    return { label: t('roles.PLAYER') || 'Player', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' };
  }
  if (role === 'GUARDIAN' || role === 'PARENT') {
    return { label: t('roles.GUARDIAN') || 'Guardian', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' };
  }
  return { label: t('roles.ADMIN') || 'Tenant Admin', color: 'bg-purple-500/15 text-purple-400 border-purple-500/30' };
});

// Check if a specific menu item is disabled due to missing active subscription
const isItemDisabled = (path: string) => {
  if (hasActiveSubscription.value) return false;
  return !path.startsWith('/dashboard/billing') && !path.startsWith('/dashboard/settings');
};

// Role-specific navigation items using translation keys exclusively
const navigationItems = computed(() => {
  const role = currentUser.value.role;

  if (role === 'PLAYER') {
    return [
      {
        name: t('nav.playerHub'),
        path: '/dashboard',
        icon: LayoutDashboard,
      },
      {
        name: t('nav.playerRoster'),
        path: '/dashboard/teams',
        icon: Users2,
      },
      {
        name: t('nav.videoAnalytics'),
        path: '/dashboard/video-analytics',
        icon: Video,
      },
      {
        name: t('nav.training'),
        path: '/dashboard/training',
        icon: Dumbbell,
      },
      {
        name: t('nav.playerLineup'),
        path: '/dashboard/lineup',
        icon: ClipboardList,
      },
      {
        name: t('nav.playerSchedule'),
        path: '/dashboard/events',
        icon: CalendarDays,
      },
      {
        name: t('nav.playerBilling'),
        path: '/dashboard/billing',
        icon: CreditCard,
      },
    ];
  }

  if (role === 'GUARDIAN' || role === 'PARENT') {
    return [
      {
        name: t('nav.parentHub'),
        path: '/dashboard',
        icon: Heart,
      },
      {
        name: t('nav.videoAnalytics'),
        path: '/dashboard/video-analytics',
        icon: Video,
      },
      {
        name: t('nav.training'),
        path: '/dashboard/training',
        icon: Dumbbell,
      },
      {
        name: t('nav.parentDirectory'),
        path: '/dashboard/teams',
        icon: Users2,
      },
      {
        name: t('nav.parentLineup'),
        path: '/dashboard/lineup',
        icon: ClipboardList,
      },
      {
        name: t('nav.parentLogistics'),
        path: '/dashboard/events',
        icon: Compass,
      },
      {
        name: t('nav.parentBilling'),
        path: '/dashboard/billing',
        icon: CreditCard,
      },
    ];
  }

  // TEAM ADMIN or COACH / ORG ADMIN
  return [
    {
      name: t('nav.dashboard'),
      path: '/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: t('nav.teams'),
      path: '/dashboard/teams',
      icon: Users2,
    },
    {
      name: t('nav.masterManagement'),
      path: '/dashboard/master-management',
      icon: Database,
    },
    {
      name: t('nav.videoAnalytics'),
      path: '/dashboard/video-analytics',
      icon: Video,
    },
    {
      name: t('nav.training'),
      path: '/dashboard/training',
      icon: Dumbbell,
    },
    {
      name: t('nav.lineup'),
      path: '/dashboard/lineup',
      icon: ClipboardList,
    },
    {
      name: t('nav.events'),
      path: '/dashboard/events',
      icon: CalendarDays,
    },
    {
      name: t('nav.billing'),
      path: '/dashboard/billing',
      icon: CreditCard,
    },
    {
      name: t('nav.settings'),
      path: '/dashboard/settings',
      icon: Settings,
    },
  ];
});

const isCurrentRoute = (path: string) => {
  if (path === '/dashboard') {
    return route.path === '/dashboard';
  }
  return route.path.startsWith(path);
};

const handleDisabledItemClick = () => {
  router.push({ path: '/dashboard/billing', query: { reason: 'subscription_required' } });
};
</script>

<template>
  <div class="min-h-screen bg-slate-950 flex flex-col md:flex-row text-slate-100 antialiased">
    <!-- Desktop Sidebar -->
    <aside
      :class="[
        'hidden md:flex flex-col bg-slate-900 border-r border-slate-800 shrink-0 sticky top-0 h-screen z-30 transition-all duration-300 shadow-2xl',
        isCollapsed ? 'w-20' : 'w-64'
      ]"
    >
      <!-- Logo Branding -->
      <div class="h-16 flex items-center px-4 border-b border-slate-800 bg-slate-900 justify-between shrink-0">
        <div class="flex items-center gap-3 overflow-hidden">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-sky-500/25 shrink-0">
            <Trophy class="w-5 h-5 text-white" />
          </div>
          <div v-if="!isCollapsed" class="flex flex-col leading-tight truncate">
            <span class="font-extrabold text-sm tracking-tight text-white">
              NEXT BASE
            </span>
            <span class="text-[10px] font-bold tracking-wider text-sky-400 uppercase">
              Team Cloud
            </span>
          </div>
        </div>

        <!-- Sidebar Collapse Toggle Button -->
        <button
          type="button"
          @click="isCollapsed = !isCollapsed"
          class="p-1.5 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition"
          :title="isCollapsed ? 'Expand' : 'Collapse'"
        >
          <ChevronLeft v-if="!isCollapsed" class="w-4 h-4" />
          <ChevronRight v-else class="w-4 h-4" />
        </button>
      </div>

      <!-- User Active Persona Card in Sidebar -->
      <div v-if="!isCollapsed" class="p-3 mx-3 mt-3 mb-1 rounded-2xl bg-slate-950/80 border border-slate-800/80 flex items-center gap-3">
        <img
          :src="currentUser.avatarUrl"
          :alt="currentUser.fullName"
          class="w-9 h-9 rounded-xl ring-2 ring-sky-500/30 object-cover shrink-0"
        />
        <div class="min-w-0 flex-1">
          <p class="font-bold text-xs text-slate-200 truncate">{{ currentUser.fullName }}</p>
          <span :class="['inline-block text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-md border mt-0.5', roleBadge.color]">
            {{ roleBadge.label }}
          </span>
        </div>
      </div>

      <!-- Inactive Subscription Notice in Sidebar -->
      <div v-if="!hasActiveSubscription && !isCollapsed" class="mx-3 my-2 p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[11px] text-amber-300 flex items-start gap-2">
        <Lock class="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
        <div class="leading-tight">
          <p class="font-bold">Subscription Inactive</p>
          <p class="text-[10px] text-slate-300 mt-0.5">Modules locked. Subscribe in Billing to unlock.</p>
        </div>
      </div>

      <!-- Navigation links -->
      <nav class="flex-1 py-3 px-3 space-y-1.5 overflow-y-auto bg-slate-900">
        <div v-if="!isCollapsed" class="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
          <span>{{ sectionTitle }}</span>
          <span v-if="!hasActiveSubscription" class="text-[9px] text-amber-400 font-bold">2/{{ navigationItems.length }} active</span>
        </div>

        <template v-for="item in navigationItems" :key="item.path">
          <!-- Active / Accessible Item -->
          <router-link
            v-if="!isItemDisabled(item.path)"
            :to="item.path"
            :class="[
              'flex items-center gap-3 px-3.5 py-2.5 rounded-xl font-semibold text-xs transition duration-150 group relative',
              isCurrentRoute(item.path)
                ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30 shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-slate-800/80 border border-transparent'
            ]"
          >
            <component :is="item.icon" class="w-4 h-4 shrink-0 transition group-hover:scale-110" />
            <span v-if="!isCollapsed" class="truncate">{{ item.name }}</span>

            <div
              v-if="isCollapsed && isCurrentRoute(item.path)"
              class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-sky-400 rounded-r-full"
            />
          </router-link>

          <!-- Disabled / Locked Item (When no active service) -->
          <div
            v-else
            @click="handleDisabledItemClick"
            :title="`Locked: Activate subscription in Billing to access ${item.name}`"
            class="flex items-center justify-between px-3.5 py-2.5 rounded-xl font-medium text-xs opacity-40 hover:opacity-75 cursor-pointer text-slate-400 hover:bg-slate-800/40 border border-transparent transition select-none"
          >
            <div class="flex items-center gap-3 truncate">
              <component :is="item.icon" class="w-4 h-4 shrink-0" />
              <span v-if="!isCollapsed" class="truncate">{{ item.name }}</span>
            </div>
            <Lock v-if="!isCollapsed" class="w-3.5 h-3.5 text-amber-400 shrink-0" />
          </div>
        </template>
      </nav>

      <!-- Sidebar Footer Org Badge -->
      <div class="p-3 border-t border-slate-800 bg-slate-950/80 shrink-0">
        <div v-if="!isCollapsed" class="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2.5">
          <div
            :class="[
              'w-2.5 h-2.5 rounded-full ring-2 shrink-0',
              hasActiveSubscription ? 'bg-emerald-400 ring-emerald-500/20 animate-pulse' : 'bg-amber-400 ring-amber-500/20'
            ]"
          />
          <div class="text-[11px] leading-tight truncate">
            <p class="font-bold text-slate-200 truncate">{{ activeTenant.name }}</p>
            <p :class="['text-[10px] font-semibold', hasActiveSubscription ? 'text-emerald-400' : 'text-amber-400']">
              {{ hasActiveSubscription ? t('nav.activeContext') : 'Awaiting Subscription' }}
            </p>
          </div>
        </div>
        <div v-else class="flex justify-center py-1">
          <div
            :class="[
              'w-3 h-3 rounded-full ring-2',
              hasActiveSubscription ? 'bg-emerald-400 ring-emerald-500/30' : 'bg-amber-400 ring-amber-500/30'
            ]"
            :title="hasActiveSubscription ? 'Tenant Active' : 'Subscription Required'"
          />
        </div>
      </div>
    </aside>

    <!-- Mobile Navigation Drawer -->
    <div
      v-if="isMobileOpen"
      class="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm md:hidden flex"
      @click.self="isMobileOpen = false"
    >
      <div class="w-72 bg-slate-900 border-r border-slate-800 h-full flex flex-col p-4 shadow-2xl animate-in slide-in-from-left duration-200">
        <div class="flex items-center justify-between pb-4 border-b border-slate-800">
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center text-white shadow-md">
              <Trophy class="w-4 h-4" />
            </div>
            <span class="font-extrabold text-sm text-white">NEXT BASE CLOUD</span>
          </div>
          <button @click="isMobileOpen = false" class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">
            <X class="w-5 h-5" />
          </button>
        </div>

        <div class="py-3 border-b border-slate-800 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <img :src="currentUser.avatarUrl" class="w-7 h-7 rounded-lg object-cover" />
            <span class="text-xs font-bold text-slate-200 truncate max-w-[120px]">{{ currentUser.fullName }}</span>
          </div>
          <LanguageSwitcher />
        </div>

        <nav class="space-y-1.5 flex-1 pt-3 overflow-y-auto">
          <div class="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
            <span>{{ sectionTitle }}</span>
            <span v-if="!hasActiveSubscription" class="text-[9px] text-amber-400 font-bold">2/{{ navigationItems.length }} active</span>
          </div>

          <template v-for="item in navigationItems" :key="item.path">
            <router-link
              v-if="!isItemDisabled(item.path)"
              :to="item.path"
              @click="isMobileOpen = false"
              :class="[
                'flex items-center gap-3 px-3.5 py-3 rounded-xl font-semibold text-sm transition',
                isCurrentRoute(item.path)
                  ? 'bg-sky-500/15 text-sky-400 border border-sky-500/30'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              ]"
            >
              <component :is="item.icon" class="w-5 h-5" />
              <span>{{ item.name }}</span>
            </router-link>

            <div
              v-else
              @click="isMobileOpen = false; handleDisabledItemClick()"
              class="flex items-center justify-between px-3.5 py-3 rounded-xl font-medium text-sm opacity-40 text-slate-400 cursor-pointer"
            >
              <div class="flex items-center gap-3">
                <component :is="item.icon" class="w-5 h-5" />
                <span>{{ item.name }}</span>
              </div>
              <Lock class="w-4 h-4 text-amber-400 shrink-0" />
            </div>
          </template>
        </nav>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col min-w-0 bg-slate-950">
      <!-- Top Navbar -->
      <header class="h-16 bg-slate-900 border-b border-slate-800 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-30 shadow-md">
        <div class="flex items-center gap-3">
          <button
            type="button"
            @click="isMobileOpen = true"
            class="md:hidden p-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-800"
          >
            <Menu class="w-5 h-5" />
          </button>

          <!-- Tenant Switcher Component -->
          <TenantSwitcher />
        </div>

        <!-- Top Right Controls -->
        <div class="flex items-center gap-3">
          <!-- Language Selector (EN / JA / ZH-TW / KO) -->
          <div class="hidden sm:block">
            <LanguageSwitcher />
          </div>

          <!-- Notification Dropdown Popover -->
          <NotificationPopover />

          <!-- Clerk User Button Menu -->
          <UserMenu />
        </div>
      </header>

      <!-- Router View Slot -->
      <main class="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto">
        <!-- GLOBAL INACTIVE SUBSCRIPTION BANNER -->
        <div
          v-if="!hasActiveSubscription"
          class="mb-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-amber-950/70 via-slate-900 to-amber-950/70 border border-amber-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl animate-in fade-in duration-300"
        >
          <div class="flex items-start sm:items-center gap-3.5">
            <div class="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
              <AlertTriangle class="w-5 h-5" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h4 class="text-xs font-black text-amber-300 uppercase tracking-wider">Subscription Required</h4>
                <span class="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono text-[10px] font-bold">
                  No Active Services
                </span>
              </div>
              <p class="text-xs text-slate-300 mt-0.5">
                No active service plan is enabled for <strong class="text-white">{{ activeTenant.name }}</strong>. All operational modules are locked until you activate a plan in Billing.
              </p>
            </div>
          </div>

          <router-link
            to="/dashboard/billing"
            class="px-4 py-2.5 rounded-xl bg-amber-500 text-slate-950 font-extrabold text-xs hover:bg-amber-400 transition flex items-center justify-center gap-1.5 shadow-lg shadow-amber-500/20 shrink-0 whitespace-nowrap"
          >
            <CreditCard class="w-4 h-4" />
            <span>Go to Billing & Activate</span>
            <ArrowRight class="w-3.5 h-3.5" />
          </router-link>
        </div>

        <router-view />
      </main>
    </div>
  </div>
</template>
